'use client';

import cx from 'classix';
import { useTranslations } from 'next-intl';
import {
  TbHomeCheck,
  TbHomeSearch,
} from 'react-icons/tb';

import rootPageSharedStyles from '@/app/[locale]/root-page-share.module.scss';
import { type ListingType } from '@/features/listing/types/listing-types';
import useAppFlyerLink from '@/features/roomster-app/hooks/use-app-flyer-link';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import Button from '@/ui/button/button';

import styles from './how-it-works-section.module.scss';

const ICONS = {
  haveshare: <TbHomeCheck />,
  needroom: <TbHomeSearch />,
};

const BUTTONS = {
  haveshare: {
    appFlyerLink: {
      linkName: 'mobileListPlace',
      mediaSource: 'mobile',
      originalLink: ROUTER_LINKS.phoneAuth,
    },
    textToken: 'list-a-room',
  },
  needroom: {
    appFlyerLink: {
      linkName: 'mobileFindPlace',
      mediaSource: 'mobile',
      originalLink: ROUTER_LINKS.phoneAuth,
    },
    textToken: 'find-a-room',
  },
} as const;

interface HowItWorksSectionProps {
  listingType: Extract<ListingType, 'haveshare' | 'needroom'>
}

export default function HowItWorksSection(props: HowItWorksSectionProps) {
  const { listingType } = props;

  const createListingTranslates = useTranslations('listing.create');
  const howItWorksSectionTranslates = useTranslations(`home.how-it-works-section.${listingType}`);
  const appFlyerLink = useAppFlyerLink(
    BUTTONS[listingType].appFlyerLink.mediaSource,
    BUTTONS[listingType].appFlyerLink.linkName,
    BUTTONS[listingType].appFlyerLink.originalLink,
  );

  return (
    <section className={cx(styles.howItWorksSection)}>
      <div className={styles.howItWorksSection_content}>
        <div className={styles.howItWorksSection_icon}>
          {ICONS[listingType]}
        </div>

        <header className={rootPageSharedStyles.header}>
          <h2 className={rootPageSharedStyles.title}>
            {howItWorksSectionTranslates('title')}
          </h2>
        </header>

        <p className={styles.howItWorksSection_description}>
          {howItWorksSectionTranslates('description')}
        </p>
      </div>

      <Button
        size="lg"
        element="link"
        href={appFlyerLink}
        aria-label={createListingTranslates(BUTTONS[listingType].textToken)}
      >
        {createListingTranslates(BUTTONS[listingType].textToken)}
      </Button>
    </section>
  );
}
